import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Chip,
  Stack
} from '@mui/material';
import {
  ArrowBack,
  TouchApp,
  Pets,
  Abc,
  Bedtime,
  Lock,
  VolumeUp,
  Instagram
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { SITE_ORIGIN, usePageSeo } from './usePageSeo';

const PAGE_TITLE = 'Baby Soundboard & Toddler Games | iPhone App for Little Kids';
const PAGE_DESCRIPTION = 'Baby Soundboard & Toddler Games is a free iPhone playroom for babies and toddlers. Big tiles, animal sounds, numbers, letters, and a bedtime game. No ads, no purchases, and nothing collected.';
const PAGE_IMAGE = `${SITE_ORIGIN}/baby-soundboard/tiles.jpg`;
const APP_STORE_URL = 'https://apps.apple.com/us/app/baby-touchtiles/id6763070482';
const INSTAGRAM_URL = 'https://www.instagram.com/babysoundboardtoddlergame';

const features = [
  {
    icon: <TouchApp />,
    title: 'Big tiles for tiny fingers',
    body: 'Every tap plays a sound, a sparkle, or a spoken word. Tiles are large, and a first tap previews so a miss is not a mistake.'
  },
  {
    icon: <Pets />,
    title: 'Animals, farm, and silly sounds',
    body: 'Touch or slide across animal, farm, and silly sound boards. Animal Pop hides the picture and asks little ones to find where it pops up next.'
  },
  {
    icon: <Abc />,
    title: 'Numbers and letters, out loud',
    body: 'Number Pop counts from 1 to 10. Alphabet Pop speaks A to Z. The voice stays on the device, and a grown-up can pick the language.'
  },
  {
    icon: <Bedtime />,
    title: 'A calm wind-down',
    body: 'Sleepy Animals is a gentle bedtime game. The rest of the playroom is wordless on the kid side, so they can explore without anything to read.'
  },
  {
    icon: <Lock />,
    title: 'Grown-up controls, behind a gate',
    body: 'Parents choose which games show and in what order, set a play timer, and lock the way out of a game. Guided Access works if you want an extra lock.'
  },
  {
    icon: <VolumeUp />,
    title: 'Nothing collected',
    body: 'No ads, no purchases, and no accounts. Settings stay on the iPhone. The app works offline and does not track anyone.'
  }
];

const BabySoundboard = () => {
  const navigate = useNavigate();

  usePageSeo({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: '/baby-soundboard',
    image: PAGE_IMAGE,
    keywords: 'baby soundboard, toddler games, baby games, animal sounds, sensory play, alphabet for toddlers, number games, iPhone kids app',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Baby Soundboard & Toddler Games',
      operatingSystem: 'iOS',
      applicationCategory: 'EducationalApplication',
      description: PAGE_DESCRIPTION,
      url: `${SITE_ORIGIN}/baby-soundboard`,
      image: PAGE_IMAGE,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      sameAs: [INSTAGRAM_URL],
      author: {
        '@type': 'Person',
        name: 'Michael R. Ellis',
        jobTitle: 'iOS Software Engineer',
        url: `${SITE_ORIGIN}/`
      }
    }
  });

  return (
    <>
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Portfolio
        </Button>

        <Box
          component="img"
          src="/baby-soundboard/header.jpg"
          alt="A row of animal tiles, a cow, dog, cat, duck, and lion, with a bunny and a panda waving"
          width={1024}
          height={438}
          decoding="async"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            borderRadius: '24px',
            mb: 3
          }}
        />

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: '24px',
            backgroundColor: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            '@media (prefers-color-scheme: dark)': {
              backgroundColor: '#1a1a1a',
              boxShadow: '0 8px 32px rgba(255,255,255,0.08)'
            }
          }}
        >
          <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto' }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.85rem', sm: '2.4rem' },
                color: '#2c3e50',
                '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
              }}
            >
              Baby Soundboard &amp; Toddler Games
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mt: 1.5,
                fontWeight: 500,
                color: 'text.secondary',
                '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' }
              }}
            >
              Big tiles for tiny fingers
            </Typography>
            <Typography
              sx={{
                mt: 2,
                fontSize: { xs: '1rem', sm: '1.1rem' },
                lineHeight: 1.7,
                color: 'text.secondary',
                '@media (prefers-color-scheme: dark)': { color: '#c8c8c8' }
              }}
            >
              A calm, wordless playroom for babies and toddlers. Hand it over and let them
              explore animals, numbers, and letters at their own pace. No ads, no purchases,
              and nothing collected.
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              justifyContent="center"
              sx={{ mt: 3 }}
            >
              {['iOS', 'Ages 5 and under', 'Offline', 'Free'].map((label) => (
                <Chip
                  key={label}
                  label={label}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: '#e8ecf4',
                    color: '#4a5366',
                    '@media (prefers-color-scheme: dark)': {
                      backgroundColor: '#333',
                      color: '#eee'
                    }
                  }}
                />
              ))}
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 3 }}
            >
              <Button
                component="a"
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#2c3e50',
                  '&:hover': { backgroundColor: '#1a2531' }
                }}
              >
                Download on the App Store
              </Button>
              <Button
                component="a"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={<Instagram />}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#2c3e50',
                  color: '#2c3e50',
                  '&:hover': { borderColor: '#1a2531', backgroundColor: 'rgba(44, 62, 80, 0.06)' },
                  '@media (prefers-color-scheme: dark)': {
                    borderColor: '#a5b4fc',
                    color: '#a5b4fc'
                  }
                }}
              >
                Instagram
              </Button>
            </Stack>
          </Box>

          <Box
            component="img"
            src="/baby-soundboard/tiles.jpg"
            alt="Baby Soundboard on three iPhones, showing animal tiles, a home screen of games, and a farm soundboard, with a waving cat and dog"
            width={1024}
            height={682}
            decoding="async"
            sx={{
              width: '100%',
              height: 'auto',
              display: 'block',
              mt: { xs: 4, sm: 5 },
              borderRadius: '20px'
            }}
          />
        </Paper>

        <Typography
          variant="h4"
          component="h2"
          sx={{
            mt: { xs: 5, sm: 7 },
            mb: 3,
            textAlign: 'center',
            fontWeight: 700,
            fontSize: { xs: '1.6rem', sm: '2rem' },
            color: '#2c3e50',
            '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
          }}
        >
          What it does
        </Typography>
        <Grid container spacing={2.5}>
          {features.map((feature) => (
            <Grid item key={feature.title} xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: '20px',
                  backgroundColor: 'white',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  '@media (prefers-color-scheme: dark)': {
                    backgroundColor: '#1a1a1a',
                    boxShadow: '0 8px 32px rgba(255,255,255,0.06)'
                  }
                }}
              >
                <Box sx={{ color: '#4a5366', mb: 1.5, '@media (prefers-color-scheme: dark)': { color: '#a5b4fc' } }}>
                  {feature.icon}
                </Box>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: '#2c3e50',
                    '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' }
                  }}
                >
                  {feature.body}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: { xs: 4, sm: 6 },
            p: { xs: 3, sm: 4 },
            borderRadius: '20px',
            textAlign: 'center',
            backgroundColor: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            '@media (prefers-color-scheme: dark)': {
              backgroundColor: '#1a1a1a',
              boxShadow: '0 8px 32px rgba(255,255,255,0.06)'
            }
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              color: '#2c3e50',
              '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
            }}
          >
            Support
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 2, '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' } }}>
            Questions about the app:{' '}
            <Box
              component="a"
              href="mailto:Contact@MichaelRobertEllis.com"
              sx={{
                color: '#4a5366',
                '@media (prefers-color-scheme: dark)': { color: '#a5b4fc' }
              }}
            >
              Contact@MichaelRobertEllis.com
            </Box>
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center" alignItems="center">
            <Button
              component="a"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Instagram />}
            >
              Instagram
            </Button>
            <Button component={RouterLink} to="/baby-soundboard-privacy-policy">
              Privacy Policy
            </Button>
          </Stack>
        </Paper>
      </Container>
    </>
  );
};

export default BabySoundboard;
